import { useState, useEffect } from 'react';
import { callRpc } from '../../lib/rpcClient';
import { Database, RefreshCw, Smartphone, Server, FileQuestion, ChevronDown, CheckCircle, Info, Trash2 } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { AlertModal } from '../../components/ui/AlertModal';

export function AdminErrors() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingId, setMarkingId] = useState<string | null>(null);
  
  // Filters
  const [filterSource, setFilterSource] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSeen, setFilterSeen] = useState<string>('all');
  
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [alertConfig, setAlertConfig] = useState<{ 
      isOpen: boolean; 
      title: string; 
      message: string;
      confirmLabel?: string;
  }>({ isOpen: false, title: '', message: '' });

  const loadData = async () => {
    try {
      setLoading(true);
      
      const now = new Date();
      // default 7 days to start
      const from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const to = now.toISOString();
      
      const result = await callRpc('get_admin_errors', { 
        p_from: from, 
        p_to: to 
      });
      
      // Filter out login-related logs (they are now in Login Times)
      const errorOnlyLogs = (result || []).filter((item: any) => 
        !item.path?.includes('/auth/') && 
        !item.context?.toLowerCase().includes('login') && 
        !item.context?.toLowerCase().includes('signin') &&
        !item.message?.toLowerCase().includes('magic link') &&
        item.source !== 'auth'
      );
      
      setData(errorOnlyLogs);
    } catch (err: any) {
      setError(err.message || 'Failed to load error logs');
    } finally {
      setLoading(false);
    }
  };

  const markAsSeen = async (ids: string[]) => {
    try {
      if (ids.length === 1) setMarkingId(ids[0]);
      await callRpc('mark_admin_error_seen', { p_error_ids: ids });
      setData(prev => prev.map(item => 
        ids.includes(item.id) ? { ...item, is_seen: true, seen_at: new Date().toISOString() } : item
      ));
    } catch (err: any) {
      setAlertConfig({
        isOpen: true,
        title: 'Error',
        message: `Failed to mark read: ${err.message}`,
        confirmLabel: 'Labi'
      });
    } finally {
      if (ids.length === 1) setMarkingId(null);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'frontend': return <Smartphone size={16} className="text-blue-400" />;
      case 'worker': return <Server size={16} className="text-purple-400" />;
      case 'supabase_rpc': return <Database size={16} className="text-green-400" />;
      case 'file_fetch': return <FileQuestion size={16} className="text-orange-400" />;
      case 'file_cleanup': return <Trash2 size={16} className="text-red-400" />;
      default: return <Info size={16} className="text-slate-400" />;
    }
  };

  const getStatusColor = (code: number | null) => {
    if (!code) return 'bg-slate-800 text-slate-400';
    if (code >= 500) return 'bg-red-500/20 text-red-500 border border-red-500/30';
    if (code === 404) return 'bg-orange-500/20 text-orange-500 border border-orange-500/30';
    if (code >= 400) return 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30';
    return 'bg-blue-500/20 text-blue-500 border border-blue-500/30';
  };

  // Apply frontend filters
  const filteredData = data.filter(item => {
    if (filterSource !== 'all' && item.source !== filterSource) return false;
    
    if (filterStatus !== 'all') {
      if (filterStatus === '5xx' && (!item.status_code || item.status_code < 500)) return false;
      if (filterStatus === '4xx' && (!item.status_code || item.status_code < 400 || item.status_code >= 500)) return false;
      if (filterStatus === '404' && item.status_code !== 404) return false;
    }
    
    if (filterSeen === 'unseen' && item.is_seen) return false;
    if (filterSeen === 'seen' && !item.is_seen) return false;
    
    return true;
  });

  const unseenFilteredIds = filteredData.filter(i => !i.is_seen).map(i => i.id);

  return (
    <div className="space-y-6 font-primary pb-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-white mb-1">Application Errors</h1>
          <p className="text-slate-400">System generated logs, 404 tracking, and blocked file cleanups.</p>
        </div>
        
        <div className="flex gap-2">
          {unseenFilteredIds.length > 0 && (
            <button 
              onClick={() => markAsSeen(unseenFilteredIds)}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl border border-slate-700 transition-colors font-bold text-sm"
            >
              Mark All Read
            </button>
          )}
          <button 
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-colors font-bold text-sm"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden flex flex-col">
        {/* Filters */}
        <div className="p-4 border-b border-slate-800 bg-slate-950/50 flex flex-wrap gap-3">
          <select 
            value={filterSource} 
            onChange={e => setFilterSource(e.target.value)}
            className="bg-slate-900 border border-slate-700 text-slate-300 text-sm rounded-lg px-3 py-2 outline-none focus:border-blue-500"
          >
            <option value="all">All Sources</option>
            <option value="frontend">Frontend API</option>
            <option value="worker">Cloudflare Worker</option>
            <option value="supabase_rpc">Supabase RPC</option>
            <option value="file_fetch">File Fetches (404s)</option>
            <option value="file_cleanup">File Cleanup Blocks</option>
          </select>

          <select 
            value={filterStatus} 
            onChange={e => setFilterStatus(e.target.value)}
            className="bg-slate-900 border border-slate-700 text-slate-300 text-sm rounded-lg px-3 py-2 outline-none focus:border-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="5xx">5xx (Server Errors)</option>
            <option value="4xx">4xx (Client Errors)</option>
            <option value="404">404 (Not Found)</option>
          </select>
          
          <select 
            value={filterSeen} 
            onChange={e => setFilterSeen(e.target.value)}
            className="bg-slate-900 border border-slate-700 text-slate-300 text-sm rounded-lg px-3 py-2 outline-none focus:border-blue-500"
          >
            <option value="all">Any State</option>
            <option value="unseen">Unseen Only</option>
            <option value="seen">Seen Only</option>
          </select>
        </div>

        {/* List */}
        <div className="flex-1 overflow-x-auto min-h-[400px]">
          {loading ? (
             <div className="flex justify-center p-12">
               <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
             </div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">{error}</div>
          ) : filteredData.length === 0 ? (
            <div className="p-12 text-center flex flex-col items-center text-slate-500">
               <CheckCircle size={48} className="text-green-500/50 mb-4" />
               <p className="font-bold">No errors found.</p>
               <p className="text-sm">Everything looks healthy based on your current filters.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800/50">
              {filteredData.map((item) => {
                const isExpanded = expandedId === item.id;
                
                return (
                  <div key={item.id} className={`flex flex-col transition-colors ${!item.is_seen ? 'bg-red-500/5' : ''} hover:bg-slate-800/30`}>
                    <div 
                      className="p-4 flex cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : item.id)}
                    >
                      {/* Left: Indicator & Base Info */}
                      <div className="flex items-center gap-4 flex-1 min-w-0 pr-4">
                        <div className="shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-slate-900 border border-slate-800" title={item.source}>
                          {getSourceIcon(item.source)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${getStatusColor(item.status_code)}`}>
                              {item.status_code || 'N/A'}
                            </span>
                            <span className="font-bold text-slate-300 text-sm truncate mr-2" title={item.path}>
                              {item.path}
                            </span>
                            {!item.is_seen && (
                              <span className="bg-red-500 w-2 h-2 rounded-full shrink-0 animate-pulse"></span>
                            )}
                          </div>
                          
                          <div className="text-xs text-slate-500 flex items-center gap-3 truncate">
                            <span className="font-medium text-slate-400">{item.context || item.source}</span>
                            <span>•</span>
                            <span>{formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}</span>
                            {item.user_email && (
                              <>
                                <span>•</span>
                                <span className="text-blue-400/80 truncate max-w-[120px] sm:max-w-[200px]">{item.user_email}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2 pr-4">
                        {!item.is_seen && (
                           <button 
                             onClick={(e) => { e.stopPropagation(); markAsSeen([item.id]); }}
                             disabled={markingId === item.id}
                             className="text-xs font-bold text-slate-400 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg px-2.5 py-1.5 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                           >
                             {markingId === item.id ? (
                               <div className="w-3 h-3 border-b-2 border-slate-300 rounded-full animate-spin"></div>
                             ) : (
                               <CheckCircle size={14} />
                             )}
                             <span className="hidden sm:inline">Resolve</span>
                           </button>
                        )}
                      </div>
                      
                      {/* Right Expand Icon */}
                      <div className="flex items-center text-slate-600">
                        <ChevronDown size={20} className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                    
                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="p-4 pt-0 border-t border-slate-800/30 bg-slate-950/20">
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Message</div>
                            <div className="text-red-300 font-mono text-xs bg-red-500/10 p-3 rounded-lg border border-red-500/20 whitespace-pre-wrap word-break">
                              {item.message || 'No specific error message provided.'}
                            </div>
                          </div>
                          
                          <div className="space-y-4">
                            <div>
                               <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Full Path / HTTP</div>
                               <div className="font-mono text-xs text-slate-300 bg-slate-900 p-2 rounded-lg border border-slate-800 overflow-x-auto">
                                 <span className="text-blue-400 font-bold mr-2">{item.method || 'GET'}</span>
                                 {item.path}
                               </div>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 text-xs text-slate-400">
                               <div>
                                 <span className="text-slate-500 mr-2">Time:</span>
                                 {format(new Date(item.created_at), 'yyyy-MM-dd HH:mm:ss')}
                               </div>
                               <div>
                                 <span className="text-slate-500 mr-2">User ID:</span>
                                 {item.user_id || 'Anonymous/Unknown'}
                               </div>
                               <div>
                                 <span className="text-slate-500 mr-2">ID:</span>
                                 <span className="font-mono">{item.id.split('-')[0]}...</span>
                               </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <AlertModal
        {...alertConfig}
        onClose={() => setAlertConfig(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
